/**
 * FB-2026-0006 (amintra · บัญชี): "เพิ่มสัญญาเช่า-แนบท้าย" จากเมนู ⋮ ของสัญญาหลัก
 *
 * สัญญาแนบท้าย = "บันทึกการส่ง–มอบ (แนบท้ายสัญญาเช่าหลัก)" ตามแบบฟอร์ม 02/03 ของผู้ใช้
 * · 1 ฉบับ = 1 เครื่อง = 1 รายการเช่า (กติกาโปรเจกต์ 1 เครื่อง = 1 สัญญาเช่า)
 * · เลือกรายการเช่าของบริษัทที่ยังไม่มีแนบท้าย → ระบบดึงยี่ห้อ/รุ่น/S/N/โปรแกรม/โครงการ/ค่าเช่า/มัดจำ/วันที่
 *   มาเติมให้ แก้ได้ก่อนบันทึก
 * · แบบเอกสาร 02 (มีเงินมัดจำ) / 03 (ไม่วางมัดจำ — มัดจำสะสมของบริษัทเกินเกณฑ์) ระบบเสนอให้ (suggested_template)
 *   ผู้ใช้สลับเองได้
 *
 * โหมดแก้ไข (addendum ส่งมา): แก้ได้เฉพาะแนบท้ายสถานะร่าง (backend PUT /contracts/{id}/addendum)
 */
import React, { useEffect, useMemo, useState } from 'react';
import Modal from '../ui/Modal';
import contractService, {
  AddendumCandidate,
  AddendumCandidatesResponse,
  AddendumPayload,
  Contract,
  DepositTemplate,
  DEPOSIT_TEMPLATE_LABEL,
} from '../../services/contractService';
import { getApiErrorMessage, getApiErrorMessageAsync } from '../../utils/mapFieldErrors';
import { getLocalDateString, formatThaiDate } from '../../utils/dateUtils';
import { showAlert } from '../../utils/alert';

export interface AddendumFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** สัญญาหลักที่จะเพิ่มแนบท้าย (โหมดสร้าง) */
  mainContract?: Pick<Contract, 'id' | 'contract_number'> | null;
  /** แนบท้ายที่จะแก้ (โหมดแก้ไข) */
  addendum?: Contract | null;
  /** เรียกหลังบันทึกสำเร็จ */
  onSaved?: (contract: Contract) => void;
}

interface FormState {
  contract_number: string;
  contract_date: string;
  start_date: string;
  first_payment_date: string;
  rental_rate: string;
  deposit_amount: string;
  /** null = ให้ระบบเลือก (ใช้ค่าที่ระบบเสนอ) */
  deposit_template: DepositTemplate | null;
  device_brand: string;
  device_model: string;
  device_serial: string;
  program_name: string;
  customer_name: string;
  min_term_months: string;
  notes: string;
}

const toDateInput = (v?: string | null): string => (v ? String(v).slice(0, 10) : '');
const str = (v: unknown): string => (v === null || v === undefined ? '' : String(v));
const money = (n: number | null | undefined) =>
  Number(n || 0).toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const emptyForm = (): FormState => ({
  contract_number: '',
  contract_date: getLocalDateString(),
  start_date: '',
  first_payment_date: '',
  rental_rate: '',
  deposit_amount: '0',
  deposit_template: null,
  device_brand: '',
  device_model: '',
  device_serial: '',
  program_name: '',
  customer_name: '',
  min_term_months: '6',
  notes: '',
});

/** ค่าจาก candidate → ฟอร์ม (ผู้ใช้แก้ต่อได้) */
export const formFromCandidate = (c: AddendumCandidate, base: FormState = emptyForm()): FormState => ({
  ...base,
  start_date: toDateInput(c.start_date),
  first_payment_date: toDateInput(c.first_payment_date),
  rental_rate: str(c.rental_rate ?? ''),
  deposit_amount: str(c.deposit_amount ?? 0),
  // null = ให้ระบบเลือกตามเงินประกันที่ "จะบันทึกจริง" (ผู้ใช้อาจแก้จากค่าที่ดึงมา) — กดเลือกเองเมื่อไรค่อยล็อก
  deposit_template: null,
  device_brand: str(c.device_brand),
  device_model: str(c.device_model),
  device_serial: str(c.device_serial),
  program_name: str(c.program_name),
  customer_name: str(c.customer_name),
  min_term_months: str(c.min_term_months || 6),
});

const formFromAddendum = (a: Contract): FormState => {
  const item = a.contract_items?.[0];
  return {
    contract_number: a.contract_number || '',
    contract_date: toDateInput(a.contract_date) || getLocalDateString(),
    start_date: toDateInput(item?.item_start_date || a.start_date),
    first_payment_date: toDateInput(item?.first_payment_date),
    rental_rate: str(item?.rental_rate ?? ''),
    deposit_amount: str(item?.deposit_amount ?? 0),
    deposit_template: (a.deposit_template as DepositTemplate) ?? null,
    device_brand: str(item?.device_brand),
    device_model: str(item?.device_model),
    device_serial: str(item?.device_serial),
    program_name: str(item?.program_name),
    customer_name: str(item?.customer_name),
    min_term_months: str(item?.rental_period_months || 6),
    notes: str(a.notes),
  };
};

const AddendumFormModal: React.FC<AddendumFormModalProps> = ({ isOpen, onClose, mainContract, addendum, onSaved }) => {
  const isEdit = Boolean(addendum);
  const mainId = isEdit ? addendum?.master_contract_id : mainContract?.id;

  const [candidates, setCandidates] = useState<AddendumCandidatesResponse | null>(null);
  const [loadingCandidates, setLoadingCandidates] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<AddendumCandidate | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm());
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState<Contract | null>(null);
  const [downloading, setDownloading] = useState(false);

  // เปิดโมดัลใหม่ = เริ่มใหม่ทุกครั้ง
  useEffect(() => {
    if (!isOpen) return;
    setErrors({});
    setSaved(null);
    setSearch('');
    setSelected(null);
    setLoadError('');
    setForm(isEdit && addendum ? formFromAddendum(addendum) : emptyForm());
    // โหลด candidates ทั้งสองโหมด — โหมดแก้ไขใช้แค่ยอดมัดจำสะสม/เกณฑ์สำหรับอธิบายแบบเอกสาร
    if (mainId) {
      setLoadingCandidates(true);
      contractService
        .getAddendumCandidates(mainId)
        .then((res) => setCandidates(res))
        .catch((err) => {
          setCandidates(null);
          setLoadError(getApiErrorMessage(err, 'โหลดรายการเช่าของบริษัทไม่สำเร็จ'));
        })
        .finally(() => setLoadingCandidates(false));
    } else {
      setCandidates(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, addendum?.id, mainId]);

  const filtered = useMemo(() => {
    const items = candidates?.items || [];
    const term = search.trim().toLowerCase();
    if (!term) return items;
    return items.filter((c) =>
      [c.rental_number, c.device_serial, c.device_brand, c.device_model, c.customer_name, c.program_name]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
        .includes(term),
    );
  }, [candidates, search]);

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((f) => ({ ...f, [key]: value }));
    if (errors[key as string]) setErrors((e) => ({ ...e, [key as string]: '' }));
  };

  const pick = (c: AddendumCandidate) => {
    setSelected(c);
    setForm((f) => formFromCandidate(c, { ...f }));
    setErrors({});
  };

  const depositNum = Number(form.deposit_amount || 0);
  const held = candidates?.deposit_held_total ?? null;
  const threshold = candidates?.deposit_waiver_threshold ?? 40000;
  // แบบที่ระบบเสนอ: คำนวณจากเงินประกันที่กรอกอยู่ตอนนี้ ด้วยเกณฑ์เดียวกับ backend
  // (services/contract_addendum.suggest_deposit_template) — เดิมยึด suggested_template ของ candidate
  // แก้มัดจำเป็น 0 แล้วยังขึ้น "ระบบเลือก 02" ทั้งที่ยอดสะสมเกินเกณฑ์ (E2E 3 พ.ย.)
  const suggested: DepositTemplate =
    depositNum === 0 && (held ?? (selected?.suggested_template === 'ACCUMULATED_WAIVER' ? threshold : -1)) >= threshold
      ? 'ACCUMULATED_WAIVER'
      : 'WITH_DEPOSIT';
  const effectiveTemplate: DepositTemplate = form.deposit_template ?? suggested;
  const waiverWithDeposit = effectiveTemplate === 'ACCUMULATED_WAIVER' && depositNum > 0;

  const validate = (): Record<string, string> => {
    const e: Record<string, string> = {};
    if (!isEdit && !selected) e.rental = 'กรุณาเลือกรายการเช่า';
    if (!form.start_date) e.start_date = 'กรุณาระบุวันเริ่มเช่า (นับระยะเช่าขั้นต่ำ)';
    if (form.rental_rate === '' || isNaN(Number(form.rental_rate)) || Number(form.rental_rate) < 0)
      e.rental_rate = 'กรุณาระบุค่าเช่าต่อเดือน (ไม่ติดลบ)';
    if (form.deposit_amount === '' || isNaN(depositNum) || depositNum < 0)
      e.deposit_amount = 'กรุณาระบุเงินประกัน/มัดจำ (ใส่ 0 ถ้าไม่มี)';
    const term = Number(form.min_term_months);
    if (!Number.isInteger(term) || term < 1) e.min_term_months = 'ระยะเช่าขั้นต่ำต้องเป็นจำนวนเดือนเต็ม ≥ 1';
    if (waiverWithDeposit) e.deposit_template = 'แบบ 03 (ไม่วางมัดจำ) ใช้ได้เมื่อเงินมัดจำของรายการนี้เป็น 0 เท่านั้น';
    setErrors(e);
    return e;
  };

  const buildPayload = (): AddendumPayload => ({
    ...(isEdit ? {} : { rental_id: selected!.rental_id, rental_item_id: selected!.rental_item_id ?? null }),
    contract_number: form.contract_number.trim() || null,
    contract_date: form.contract_date || null,
    start_date: form.start_date,
    first_payment_date: form.first_payment_date || null,
    rental_rate: Number(form.rental_rate),
    deposit_amount: depositNum,
    deposit_template: form.deposit_template,
    device_brand: form.device_brand.trim() || null,
    device_model: form.device_model.trim() || null,
    device_serial: form.device_serial.trim() || null,
    program_name: form.program_name.trim() || null,
    customer_name: form.customer_name.trim() || null,
    min_term_months: Number(form.min_term_months),
    notes: form.notes.trim() || null,
  });

  const handleSave = async () => {
    if (saving) return;
    const e = validate();
    if (Object.keys(e).length > 0) return;
    setSaving(true);
    try {
      const payload = buildPayload();
      const result = isEdit
        ? await contractService.updateAddendum(addendum!.id, payload)
        : await contractService.createAddendum(mainId!, payload);
      setSaved(result);
      onSaved?.(result);
      showAlert(
        isEdit ? 'แก้ไขสัญญาแนบท้ายสำเร็จ' : 'เพิ่มสัญญาแนบท้ายสำเร็จ',
        `บันทึกเลขที่ ${result.contract_number} เรียบร้อยแล้ว · ดาวน์โหลด PDF ได้จากปุ่มด้านล่าง`,
        'success',
      );
    } catch (err) {
      showAlert('บันทึกไม่สำเร็จ', getApiErrorMessage(err, 'ไม่สามารถบันทึกสัญญาแนบท้ายได้'), 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDownload = async () => {
    if (!saved) return;
    setDownloading(true);
    try {
      await contractService.downloadAndSaveContractPDF(saved);
    } catch (err) {
      showAlert('เกิดข้อผิดพลาด', await getApiErrorMessageAsync(err, 'ไม่สามารถดาวน์โหลดไฟล์ PDF ได้'), 'error');
    } finally {
      setDownloading(false);
    }
  };

  const title = isEdit
    ? `แก้ไขสัญญาแนบท้าย ${addendum?.contract_number || ''}`
    : `เพิ่มสัญญาแนบท้าย — สัญญาหลัก ${mainContract?.contract_number || ''}`;

  const inputCls = (key: string) => `input ${errors[key] ? 'border-danger-500' : ''}`;
  const err = (key: string) =>
    errors[key] ? (
      <p id={`addendum-${key}-error`} className="mt-1 text-xs text-danger-600">{errors[key]}</p>
    ) : null;

  const footer = saved ? (
    <div className="flex flex-col sm:flex-row gap-2 sm:justify-end w-full">
      <button type="button" onClick={handleDownload} disabled={downloading}
        className="px-4 py-2 rounded-lg bg-primary-600 text-white hover:bg-primary-700 disabled:bg-secondary-400">
        {downloading ? 'กำลังสร้าง PDF...' : 'ดาวน์โหลด PDF บันทึกส่ง-มอบ'}
      </button>
      <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg border border-secondary-300 text-secondary-700 hover:bg-secondary-50">
        ปิด
      </button>
    </div>
  ) : (
    <div className="flex flex-col sm:flex-row gap-2 sm:justify-end w-full">
      <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg border border-secondary-300 text-secondary-700 hover:bg-secondary-50">
        ยกเลิก
      </button>
      <button type="button" onClick={handleSave} disabled={saving || (!isEdit && !selected)}
        className="px-4 py-2 rounded-lg bg-primary-600 text-white hover:bg-primary-700 disabled:bg-secondary-400">
        {saving ? 'กำลังบันทึก...' : isEdit ? 'บันทึกการแก้ไข' : 'บันทึกสัญญาแนบท้าย'}
      </button>
    </div>
  );

  const showForm = isEdit || Boolean(selected);

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={title} size="4xl" footer={footer}
      subtitle="บันทึกการส่ง–มอบ (แนบท้ายสัญญาเช่าหลัก) · 1 ฉบับต่อ 1 เครื่อง/รายการเช่า">
      <div className="space-y-5" data-testid="addendum-form-modal">
        {saved && (
          <div className="rounded-md border border-success-200 bg-success-50 p-3 text-sm text-success-800" role="status">
            บันทึกสัญญาแนบท้ายเลขที่ <strong>{saved.contract_number}</strong> แล้ว (สถานะร่าง — อนุมัติได้ที่หน้าจัดการสัญญา)
          </div>
        )}

        {/* 1) เลือกรายการเช่า (เฉพาะโหมดสร้าง) */}
        {!isEdit && !saved && (
          <section aria-labelledby="addendum-pick-heading">
            <h3 id="addendum-pick-heading" className="text-sm font-semibold text-secondary-800 mb-2">
              1. เลือกรายการเช่า {candidates?.company_name ? `ของ ${candidates.company_name}` : ''}
            </h3>
            {loadingCandidates ? (
              <p className="text-sm text-secondary-500">กำลังโหลดรายการเช่า...</p>
            ) : loadError ? (
              <p className="text-sm text-danger-600" role="alert">{loadError}</p>
            ) : (candidates?.items.length || 0) === 0 ? (
              <p className="text-sm text-secondary-600">
                ไม่มีรายการเช่าที่ใช้งานอยู่ซึ่งยังไม่มีสัญญาแนบท้ายสำหรับบริษัทนี้
              </p>
            ) : (
              <>
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="ค้นหา: เลขที่เช่า / S/N / ยี่ห้อ รุ่น / โครงการ"
                  aria-label="ค้นหารายการเช่าสำหรับสัญญาแนบท้าย"
                  className="input mb-2"
                />
                <div className="max-h-56 overflow-y-auto border border-secondary-200 rounded-lg divide-y divide-secondary-100" role="listbox" aria-label="รายการเช่าที่ยังไม่มีสัญญาแนบท้าย">
                  {filtered.map((c) => {
                    const active = selected?.rental_id === c.rental_id;
                    return (
                      <button
                        type="button"
                        role="option"
                        aria-selected={active}
                        key={c.rental_id}
                        onClick={() => pick(c)}
                        className={`w-full text-left px-3 py-2 text-sm hover:bg-secondary-50 ${active ? 'bg-info-50' : ''}`}
                      >
                        <span className="font-semibold text-secondary-900">{c.rental_number}</span>
                        <span className="text-secondary-700"> · S/N {c.device_serial || '-'}</span>
                        <span className="text-secondary-700"> · {[c.device_brand, c.device_model].filter(Boolean).join(' ') || 'ไม่ระบุยี่ห้อ/รุ่น'}</span>
                        <span className="text-secondary-600"> · โครงการ {c.customer_name || '-'}</span>
                        {(c.item_count || 0) > 1 && (
                          <span className="ml-1 text-xs text-warning-700">(มี {c.item_count} รายการในสัญญาเช่านี้ — ใช้เครื่องแรก)</span>
                        )}
                      </button>
                    );
                  })}
                  {filtered.length === 0 && (
                    <p className="px-3 py-2 text-sm text-secondary-500">ไม่พบรายการที่ตรงกับคำค้น</p>
                  )}
                </div>
                {errors.rental && <p className="mt-1 text-xs text-danger-600">{errors.rental}</p>}
              </>
            )}
          </section>
        )}

        {/* 2) รายละเอียดบันทึกส่ง-มอบ */}
        {showForm && !saved && (
          <section aria-labelledby="addendum-detail-heading">
            <h3 id="addendum-detail-heading" className="text-sm font-semibold text-secondary-800 mb-2">
              {isEdit ? 'รายละเอียดบันทึกส่ง-มอบ' : `2. รายละเอียดบันทึกส่ง-มอบ (ดึงจากรายการเช่า ${selected?.rental_number} — แก้ได้)`}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label htmlFor="addendum-contract_number" className="block text-xs font-medium text-secondary-700 mb-1">เลขที่บันทึก</label>
                <input id="addendum-contract_number" className={inputCls('contract_number')} value={form.contract_number}
                  onChange={(e) => set('contract_number', e.target.value)} placeholder="ว่าง = ระบบออกเลขให้" maxLength={50} />
              </div>
              <div>
                <label htmlFor="addendum-contract_date" className="block text-xs font-medium text-secondary-700 mb-1">วันที่บันทึก</label>
                <input id="addendum-contract_date" type="date" className={inputCls('contract_date')} value={form.contract_date}
                  onChange={(e) => set('contract_date', e.target.value)} />
              </div>
              <div>
                <label htmlFor="addendum-customer_name" className="block text-xs font-medium text-secondary-700 mb-1">ใช้กับ "ลูกค้า" (โครงการ)</label>
                <input id="addendum-customer_name" className={inputCls('customer_name')} value={form.customer_name}
                  onChange={(e) => set('customer_name', e.target.value)} />
              </div>
              <div>
                <label htmlFor="addendum-device_brand" className="block text-xs font-medium text-secondary-700 mb-1">ยี่ห้อ</label>
                <input id="addendum-device_brand" className={inputCls('device_brand')} value={form.device_brand}
                  onChange={(e) => set('device_brand', e.target.value)} />
              </div>
              <div>
                <label htmlFor="addendum-device_model" className="block text-xs font-medium text-secondary-700 mb-1">รุ่น</label>
                <input id="addendum-device_model" className={inputCls('device_model')} value={form.device_model}
                  onChange={(e) => set('device_model', e.target.value)} />
              </div>
              <div>
                <label htmlFor="addendum-device_serial" className="block text-xs font-medium text-secondary-700 mb-1">หมายเลขเครื่อง (S/N)</label>
                <input id="addendum-device_serial" className={inputCls('device_serial')} value={form.device_serial}
                  onChange={(e) => set('device_serial', e.target.value)} />
              </div>
              <div className="md:col-span-3">
                <label htmlFor="addendum-program_name" className="block text-xs font-medium text-secondary-700 mb-1">พร้อมโปรแกรม</label>
                <input id="addendum-program_name" className={inputCls('program_name')} value={form.program_name}
                  onChange={(e) => set('program_name', e.target.value)} />
              </div>
              <div>
                <label htmlFor="addendum-start_date" className="block text-xs font-medium text-secondary-700 mb-1">
                  เช่าขั้นต่ำนับตั้งแต่วันที่ <span className="text-danger-500">*</span>
                </label>
                <input id="addendum-start_date" type="date" className={inputCls('start_date')} value={form.start_date}
                  onChange={(e) => set('start_date', e.target.value)}
                  aria-invalid={Boolean(errors.start_date)} aria-describedby={errors.start_date ? 'addendum-start_date-error' : undefined} />
                {err('start_date')}
              </div>
              <div>
                <label htmlFor="addendum-min_term_months" className="block text-xs font-medium text-secondary-700 mb-1">ระยะเช่าขั้นต่ำ (เดือน)</label>
                <input id="addendum-min_term_months" type="number" min={1} step={1} className={inputCls('min_term_months')} value={form.min_term_months}
                  onChange={(e) => set('min_term_months', e.target.value)}
                  aria-invalid={Boolean(errors.min_term_months)} aria-describedby={errors.min_term_months ? 'addendum-min_term_months-error' : undefined} />
                {err('min_term_months')}
              </div>
              <div>
                <label htmlFor="addendum-first_payment_date" className="block text-xs font-medium text-secondary-700 mb-1">เริ่มชำระค่าเช่างวดแรกวันที่</label>
                <input id="addendum-first_payment_date" type="date" className={inputCls('first_payment_date')} value={form.first_payment_date}
                  onChange={(e) => set('first_payment_date', e.target.value)} />
              </div>
              <div>
                <label htmlFor="addendum-rental_rate" className="block text-xs font-medium text-secondary-700 mb-1">
                  ค่าเช่าเดือนละ (บาท · ยังไม่รวม VAT) <span className="text-danger-500">*</span>
                </label>
                <input id="addendum-rental_rate" type="number" min={0} step="0.01" className={inputCls('rental_rate')} value={form.rental_rate}
                  onChange={(e) => set('rental_rate', e.target.value)}
                  aria-invalid={Boolean(errors.rental_rate)} aria-describedby={errors.rental_rate ? 'addendum-rental_rate-error' : undefined} />
                {err('rental_rate')}
              </div>
              <div>
                <label htmlFor="addendum-deposit_amount" className="block text-xs font-medium text-secondary-700 mb-1">
                  เงินประกัน/มัดจำต่อเครื่อง (บาท) <span className="text-danger-500">*</span>
                </label>
                <input id="addendum-deposit_amount" type="number" min={0} step="0.01" className={inputCls('deposit_amount')} value={form.deposit_amount}
                  onChange={(e) => set('deposit_amount', e.target.value)}
                  aria-invalid={Boolean(errors.deposit_amount)} aria-describedby={errors.deposit_amount ? 'addendum-deposit_amount-error' : undefined} />
                {err('deposit_amount')}
              </div>
              <div className="md:col-span-3">
                <label htmlFor="addendum-notes" className="block text-xs font-medium text-secondary-700 mb-1">หมายเหตุ</label>
                <textarea id="addendum-notes" rows={2} className="input" value={form.notes} onChange={(e) => set('notes', e.target.value)} />
              </div>
            </div>

            {/* 3) แบบเอกสาร 02 / 03 */}
            <fieldset className="mt-4 rounded-lg border border-secondary-200 p-3" aria-describedby="addendum-template-reason">
              <legend className="px-1 text-sm font-semibold text-secondary-800">แบบเอกสาร (ข้อเงินประกัน)</legend>
              <p id="addendum-template-reason" className="text-xs text-secondary-600 mb-2" data-testid="addendum-template-reason">
                ระบบเลือก: <strong>{DEPOSIT_TEMPLATE_LABEL[suggested]}</strong> — มัดจำรายการนี้ {money(depositNum)} บาท
                {held !== null && (
                  <> · ยอดมัดจำสะสมที่บริษัทวางไว้ {money(held)} บาท / เกณฑ์ {money(threshold)} บาท</>
                )}
                {' '}(แบบ 03 ใช้เมื่อมัดจำรายการนี้เป็น 0 และยอดสะสมถึงเกณฑ์)
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                {(['WITH_DEPOSIT', 'ACCUMULATED_WAIVER'] as DepositTemplate[]).map((t) => (
                  <label key={t} className="inline-flex items-center gap-2 text-sm cursor-pointer">
                    <input
                      type="radio"
                      name="addendum-deposit-template"
                      value={t}
                      checked={effectiveTemplate === t}
                      onChange={() => set('deposit_template', t)}
                    />
                    <span>{t === 'WITH_DEPOSIT' ? '02 มีเงินมัดจำ' : '03 ไม่วางมัดจำ — มัดจำสะสมเกินเกณฑ์'}</span>
                    {t === suggested && <span className="text-xs text-primary-700">(ระบบเสนอ)</span>}
                  </label>
                ))}
              </div>
              {waiverWithDeposit && (
                <p className="mt-2 text-xs font-medium text-warning-800" role="alert" data-testid="addendum-waiver-warning">
                  เลือกแบบ 03 (ไม่วางมัดจำ) แต่รายการนี้มีเงินมัดจำ {money(depositNum)} บาท — เปลี่ยนมัดจำเป็น 0 หรือใช้แบบ 02
                </p>
              )}
              {err('deposit_template')}
            </fieldset>

            {candidates?.master_contract_number && (
              <p className="mt-3 text-xs text-secondary-500">
                อ้างอิงสัญญาเช่าหลักเลขที่ {candidates.master_contract_number}
                {candidates.master_contract_date ? ` ลงวันที่ ${formatThaiDate(candidates.master_contract_date)}` : ''}
              </p>
            )}
          </section>
        )}
      </div>
    </Modal>
  );
};

export default AddendumFormModal;
